import React, { useState, useEffect, useCallback } from 'react';
import usePersistentState from '../utils/usePersistentState';
import { activityAPI } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { formatActor } from '../utils/attribution';
import { formatChangeValue } from '../utils/html';
import { prettyFieldLabel } from '../utils/labels';
import { Select, Spinner, Card, Pagination, EmptyState, Badge } from '../components/UI';

const moduleColor = {
  Client: 'info', Candidate: 'purple', Requirement: 'success', User: 'warning', Auth: 'default'
};

const FIELD_LABELS = {
  jobTitle: 'Job Title', status: 'Status', priority: 'Priority',
  budgetMin: 'Min Budget', budgetMax: 'Max Budget', budgetCurrency: 'Currency',
  minExperience: 'Min Experience', maxExperience: 'Max Experience',
  openings: 'Openings', requiredSkills: 'Required Skills',
  jobDescription: 'Job Description', location: 'Location', workMode: 'Work Mode',
  clientId: 'Client', name: 'Name', email: 'Email', role: 'Role',
  phone: 'Phone', clientName: 'Client Name', contactEmail: 'Contact Email',
  skills: 'Skills', experience: 'Experience',
};

const formatValue = (field, val) => {
  if (val === null || val === undefined || val === '') return '—';
  if (field.toLowerCase().includes('experience') && typeof val !== 'object') return `${val} yr${val !== 1 ? 's' : ''}`;
  // Handles HTML, arrays, and objects (e.g. contacts) — never "[object Object]".
  return formatChangeValue(val) || '—';
};

const buildDescription = (log, showAttribution = true) => {
  const actor = formatActor(log.user || { name: 'Someone' }, log.impersonatedBy, showAttribution);
  const target = log.targetName ? `"${log.targetName}"` : '';
  const module = log.module || '';
  const changes = log.details?.changes || {};
  const changedKeys = Object.keys(changes);

  switch (log.action) {
    case 'CREATE_REQUIREMENT': return `${actor} created a new ${module} ${target}.`;
    case 'UPDATE_REQUIREMENT': {
      if (changedKeys.length === 0) return `${actor} updated ${module} ${target}.`;
      const parts = changedKeys.slice(0, 3).map(f => {
        const label = FIELD_LABELS[f] || f;
        return `${label} from "${formatValue(f, changes[f].before)}" → "${formatValue(f, changes[f].after)}"`;
      });
      const extra = changedKeys.length > 3 ? ` and ${changedKeys.length - 3} more` : '';
      return `${actor} updated ${module} ${target} — changed ${parts.join(', ')}${extra}.`;
    }
    case 'DELETE_REQUIREMENT':   return `${actor} deleted ${module} ${target}.`;
    case 'CREATE_CLIENT':        return `${actor} added a new client ${target}.`;
    case 'UPDATE_CLIENT': {
      if (changedKeys.length === 0) return `${actor} updated client ${target}.`;
      const parts = changedKeys.slice(0, 2).map(f => {
        const label = FIELD_LABELS[f] || f;
        return `${label} from "${formatValue(f, changes[f].before)}" → "${formatValue(f, changes[f].after)}"`;
      });
      const extra = changedKeys.length > 2 ? ` and ${changedKeys.length - 2} more` : '';
      return `${actor} updated client ${target} — changed ${parts.join(', ')}${extra}.`;
    }
    case 'DELETE_CLIENT':        return `${actor} deleted client ${target}.`;
    case 'CREATE_CANDIDATE':     return `${actor} added candidate ${target}.`;
    case 'UPDATE_CANDIDATE': {
      if (changedKeys.length === 0) return `${actor} updated candidate ${target}.`;
      const parts = changedKeys.slice(0, 2).map(f => {
        const label = FIELD_LABELS[f] || f;
        return `${label} from "${formatValue(f, changes[f].before)}" → "${formatValue(f, changes[f].after)}"`;
      });
      const extra = changedKeys.length > 2 ? ` and ${changedKeys.length - 2} more` : '';
      return `${actor} updated candidate ${target} — changed ${parts.join(', ')}${extra}.`;
    }
    case 'DELETE_CANDIDATE':     return `${actor} deleted candidate ${target}.`;
    case 'CREATE_USER':          return `${actor} created user account ${target}.`;
    case 'UPDATE_USER': {
      if (changedKeys.length === 0) return `${actor} updated user ${target}.`;
      const parts = changedKeys.slice(0, 2).map(f => {
        const label = FIELD_LABELS[f] || f;
        return `${label} from "${formatValue(f, changes[f].before)}" → "${formatValue(f, changes[f].after)}"`;
      });
      return `${actor} updated user ${target} — changed ${parts.join(', ')}.`;
    }
    case 'DELETE_USER':          return `${actor} deleted user ${target}.`;
    case 'LOGIN':                return `${actor} logged in.`;
    case 'LOGOUT':               return `${actor} logged out.`;
    case 'LOGIN_FAILED':         return `Failed login attempt for ${target || 'unknown user'}.`;
    default:                     return `${actor} performed ${log.action}${target ? ` on ${target}` : ''}.`;
  }
};

// ── Diff table ──────────────────────────────────────────────────────────────
const DiffTable = ({ changes, isDark }) => {
  const keys = Object.keys(changes);
  if (keys.length === 0) return null;

  return (
    <div style={{
      marginTop: 12, borderRadius: 8, overflow: 'hidden',
      border: '1px solid var(--border-subtle)',
    }}>
      <div style={{ overflowX: 'auto' }}>
        {/* Header row */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
          minWidth: 480,
          background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc',
          padding: '6px 12px',
          borderBottom: '1px solid var(--border-subtle)',
        }}>
          {['Field', 'Before', 'After'].map(h => (
            <span key={h} style={{
              fontSize: 10, fontWeight: 600, color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.06em',
            }}>{h}</span>
          ))}
        </div>
        {/* Data rows */}
        {keys.map((field, i) => (
          <div
            key={field}
            style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
              minWidth: 480,
              padding: '8px 12px',
              borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)',
              background: 'transparent',
              transition: 'background 0.12s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>
              {FIELD_LABELS[field] || prettyFieldLabel(field)}
            </span>
            <span
              title={String(formatValue(field, changes[field].before))}
              style={{
                fontSize: 12, color: '#ef4444', textDecoration: 'line-through',
                wordBreak: 'break-word', overflowWrap: 'anywhere', paddingRight: 8, minWidth: 0,
              }}
            >
              {formatValue(field, changes[field].before)}
            </span>
            <span
              title={String(formatValue(field, changes[field].after))}
              style={{
                fontSize: 12, fontWeight: 500, color: '#16a34a',
                wordBreak: 'break-word', overflowWrap: 'anywhere', minWidth: 0,
              }}
            >
              {formatValue(field, changes[field].after)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Main page ───────────────────────────────────────────────────────────────
export default function ActivityLogs() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { isSuperAdmin, isRecruiterManager, isRecruiter, showAttribution } = useAuth();

  if (!isSuperAdmin() && !isRecruiterManager() && !isRecruiter()) {
    return (
      <div style={{ padding: '48px 24px', textAlign: 'center' }}>
        <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>Access Denied</p>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>You do not have permission to view activity logs.</p>
      </div>
    );
  }

  const [logs, setLogs]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [pages, setPages]         = useState(1);
  const [page, setPage]           = useState(1);
  // Kept for this tab — survives leaving the page and coming back.
  const [moduleFilter, setModuleFilter] = usePersistentState('activityLogs:module', '');
  const [loading, setLoading]     = useState(true);
  const [expandedIds, setExpandedIds] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await activityAPI.getAll({ page, limit: 20, module: moduleFilter });
      setLogs(data.logs);
      setTotal(data.total);
      setPages(data.pages);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [page, moduleFilter]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>Activity Logs</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{total} total activities</p>
        </div>
      </div>

      <Select
        value={moduleFilter}
        onChange={e => { setModuleFilter(e.target.value); setPage(1); }}
        className="w-full sm:w-48"
      >
        <option value="">All Modules</option>
        <option>Client</option>
        <option>Candidate</option>
        <option>Requirement</option>
        <option>User</option>
        <option>Auth</option>
      </Select>

      <Card>
        {loading ? <div className="py-16 flex justify-center"><Spinner /></div> : logs.length === 0 ? (
          <EmptyState icon="📝" title="No activity logs" message="Actions will appear here." />
        ) : (
          <div className="space-y-2">
            {logs.map(log => {
              const hasChanges = log.details?.changes && Object.keys(log.details.changes).length > 0;
              const isExpanded = expandedIds.has(log.id);

              return (
                <div
                  key={log.id}
                  style={{
                    padding: '14px 16px',
                    borderRadius: 10,
                    background: 'var(--bg-raised)',
                    border: '1px solid var(--border-subtle)',
                    cursor: hasChanges ? 'pointer' : 'default',
                    transition: 'background 0.15s ease, border-color 0.15s ease',
                  }}
                  onClick={() => hasChanges && toggleExpand(log.id)}
                  onMouseEnter={e => { if (hasChanges) { e.currentTarget.style.borderColor = 'var(--border-brand)'; e.currentTarget.style.background = 'var(--bg-hover)'; } }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.background = 'var(--bg-raised)'; }}
                >
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div style={{
                      width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                      background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 13, fontWeight: 700, marginTop: 1,
                      boxShadow: '0 2px 8px rgba(99,102,241,0.25)',
                    }}>
                      {log.user?.name?.[0]?.toUpperCase() || '?'}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Top row */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                          {formatActor(log.user, log.impersonatedBy, showAttribution)}
                        </span>
                        {/* Action chip */}
                        <span style={{
                          fontSize: 11, fontFamily: 'monospace',
                          background: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                          color: 'var(--text-secondary)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 6, padding: '2px 7px', fontWeight: 500,
                        }}>
                          {log.action}
                        </span>
                        {log.module && <Badge variant={moduleColor[log.module]} size="sm">{log.module}</Badge>}
                        {log.targetName && (
                          <span style={{ fontSize: 12, color: 'var(--text-secondary)', wordBreak: 'break-word', overflowWrap: 'anywhere', minWidth: 0 }}>
                            → {log.targetName}
                          </span>
                        )}
                        {hasChanges && (
                          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)', userSelect: 'none' }}>
                            {isExpanded ? '▲ hide' : '▼ show changes'}
                          </span>
                        )}
                      </div>

                      {/* Description */}
                      <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {buildDescription(log, showAttribution)}
                      </p>

                      {/* Timestamp */}
                      <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                        {new Date(log.createdAt).toLocaleString()}
                      </p>

                      {/* Expandable diff */}
                      {hasChanges && isExpanded && (
                        <DiffTable changes={log.details.changes} isDark={isDark} />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Pagination page={page} pages={pages} onPage={setPage} />
    </div>
  );
}
